<template>
    <div class="add-alert-container">
        <form action="" @submit.prevent="onSubmit()">
            <!-- Step 1: Alert Type and Name -->
            <div class="form-step-one">
                <b-row>
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Alert Contact Type <span class="required">*</span></label>
                            <div class="custom-select-wrapper">
                                <img src="@/assets/icons/alert-selector-bell.svg" alt="Alert" class="select-icon" />
                                <b-form-select 
                                    name="type" 
                                    v-model="input.type" 
                                    :options="alert_opt" 
                                    :state="validate_type" 
                                    :disabled="edit_alert" 
                                    class="custom-form-select"
                                    required>
                                </b-form-select>
                                <svg class="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <b-form-invalid-feedback :state="validate_type">Select the alert contact type</b-form-invalid-feedback>
                        </div>
                    </b-col>
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Friendly Name <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isNameEntered }">
                                <b-form-input 
                                    type="text" 
                                    name="name" 
                                    v-model="input.name" 
                                    :state="null"  
                                    @input="validateName"  
                                    maxlength="50" 
                                    placeholder="Enter Name" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isNameEntered" class="validation-icon">
                                    <svg v-if="!invalid_fname_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isNameEntered">{{ invalid_fname_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                </b-row>
            </div>
            
            
            <!-- Step 2: Additional Fields - Only show when type and name are filled -->
            <div v-if="showAdditionalFields" class="form-step-two">
                <!-- Webhook Secret Key -->
                <b-row v-if="input.type == 5">
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Secret Key <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isKeyEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.sk_webhook" 
                                    maxlength="80" 
                                    placeholder="Secret Key" 
                                    :state="null" 
                                    @input="validateKey" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isKeyEntered" class="validation-icon">
                                    <svg v-if="!invalid_key_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isKeyEntered">{{ invalid_key_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                </b-row>

                <!-- Alert To URL Field -->
                <b-row v-if="input.type != 4">
                    <b-col md="12">
                        <div class="form-group">
                            <label class="form-label">
                                <span v-if="input.type">{{ related_id_lable[input.type].text }}</span>
                                <span class="required"> *</span>
                            </label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isUrlEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.alertToUrl" 
                                    :placeholder="inputPlaceholder"
                                    :state="null"
                                    @input="validateAlertUrl"
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isUrlEntered" class="validation-icon">
                                    <svg v-if="!invalid_url_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isUrlEntered">{{ invalid_url_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                </b-row>

                <!-- Zulip Fields -->
                <b-row v-if="input.type == 10">
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Key <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isZulipKeyEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.key" 
                                    maxlength="80" 
                                    placeholder="Key" 
                                    :state="null" 
                                    @input="validateZulipKey" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isZulipKeyEntered" class="validation-icon">
                                    <svg v-if="!invalid_zulip_key_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isZulipKeyEntered">{{ invalid_zulip_key_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Site <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isSiteEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.site" 
                                    maxlength="80" 
                                    placeholder="Site" 
                                    :state="null" 
                                    @input="validateSite" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isSiteEntered" class="validation-icon">
                                    <svg v-if="!invalid_site_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isSiteEntered">{{ invalid_site_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 10">
                    <b-col md="6">
                        <div class="form-group">
                            <label class="form-label">Channel <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isChannelEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.channel" 
                                    maxlength="80" 
                                    placeholder="Channel" 
                                    :state="null" 
                                    @input="validateChannel" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isChannelEntered" class="validation-icon">
                                    <svg v-if="!invalid_channel_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isChannelEntered">{{ invalid_channel_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                    <b-col md="6" v-if="input.type == 10">
                        <div class="form-group">
                            <label class="form-label">Topic <span class="required">*</span></label>
                            <div class="custom-input-wrapper" :class="{ 'has-validation': isTopicEntered }">
                                <b-form-input 
                                    type="text" 
                                    v-model="input.topic" 
                                    maxlength="80" 
                                    placeholder="Topic" 
                                    :state="null" 
                                    @input="validateTopic" 
                                    class="custom-form-input"
                                    required>
                                </b-form-input>
                                <span v-if="isTopicEntered" class="validation-icon">
                                    <svg v-if="!invalid_topic_message" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#10B981" stroke="#10B981" stroke-width="2"/>
                                        <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="10" cy="10" r="9" fill="#EF4444" stroke="#EF4444" stroke-width="2"/>
                                        <path d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </span>
                            </div>
                            <b-form-invalid-feedback v-if="isTopicEntered">{{ invalid_topic_message }}</b-form-invalid-feedback>
                        </div>
                    </b-col>
                </b-row>
                
                <!-- Divider before Notes Section -->
                <hr v-if="showAdditionalFields && input.type" class="notes-divider">
                
                <!-- Notes Section -->
                <b-row v-if="input.type == 1">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header-simple">
                                <span class="note-title"><span>Note</span> : Please enter Email Id above.</span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <!-- <b-row v-if="input.type == 2">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header" @click="toggleNote('skype')">
                                <span class="note-title"><span>Note</span> : How to add Skype alert</span>
                                <svg class="note-arrow" :class="{ 'rotated': expandedNotes.skype }" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div v-if="expandedNotes.skype" class="note-content">
                                <ol class="note-list">
                                    <li>Please enter Skype Id above.</li>
                                    <li>Example: <span class="text-info">live:{skypeid}</span></li>
                                </ol>
                            </div>
                        </div>
                    </b-col>
                </b-row> -->
                
                <b-row v-if="input.type == 3">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header" @click="toggleNote('telegram')">
                                <span class="note-title"><span>Note</span> : Here the some of steps</span>
                                <svg class="note-arrow" :class="{ 'rotated': expandedNotes.telegram }" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div v-if="expandedNotes.telegram" class="note-content">
                                <ol class="note-list">
                                    <li>Please get telegram username from telegram profile.</li>
                                    <li>Your telegram username will look like <span class="text-info">@demousername</span>, but enter it without the <span class="text-danger">@</span>.</li>
                                    <li>Validation - You can use a-z, 0-9 and underscores. Minimum length is 5 characters and maximum length 32 characters.</li>
                                </ol>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 4">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header-simple">
                                <span class="note-title"><span>Note</span> : Please enter friendly name only.</span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 5">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header" @click="toggleNote('webhook')">
                                <span class="note-title"><span>Note</span> : Webhook configuration</span>
                                <svg class="note-arrow" :class="{ 'rotated': expandedNotes.webhook }" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div v-if="expandedNotes.webhook" class="note-content">
                                <ol class="note-list">
                                    <li v-if="input.type">Please enter url in {{ related_id_lable[input.type].text }}.</li>
                                    <li v-if="input.type">Enter Secret Key – set your own secret key (like a password) to ensure alerts are verified and from the right source.</li>
                                </ol>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 6 || input.type == 7">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header" @click="toggleNote('phone')">
                                <span class="note-title"><span>Note</span> : Phone number format</span>
                                <svg class="note-arrow" :class="{ 'rotated': expandedNotes.phone }" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div v-if="expandedNotes.phone" class="note-content">
                                <ol class="note-list">
                                    <li v-if="input.type">Please enter Mobile number with country code in {{ related_id_lable[input.type].text }}.</li>
                                    <li>for example : <span class="text-info">+19999999999</span></li>
                                </ol>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 8">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header-simple">
                                <span class="note-title"><span>Note</span> : Please enter email in {{ related_id_lable[input.type].text }}.</span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 9">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header-simple">
                                <span class="note-title"><span>Note</span> : Please enter url in {{ related_id_lable[input.type].text }}.</span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 10">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header" @click="toggleNote('zulip')">
                                <span class="note-title"><span>Note</span> : Here are the steps</span>
                                <svg class="note-arrow" :class="{ 'rotated': expandedNotes.zulip }" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 10L12 15L17 10" stroke="#3A3A3A" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <div v-if="expandedNotes.zulip" class="note-content">
                                <ol class="note-list">
                                    <li>Go to <b>Settings</b> > <b>Organization/Personal settings</b> > <b>Bots</b>.</li>
                                    <li>Click <b>Add new bot</b>.</li>
                                    <li>Click <b>bot settings</b> link, note down below configurations -
                                        <ol>
                                            <li><b>Email</b></li>
                                            <li><b>API key</b></li>
                                            <li><b>Site/URL</b></li>
                                        </ol>
                                    </li>
                                    <li>Navigate to the channel where you want bot to post the alert.</li>
                                    <li>Click the <b>...</b> at the top right corner, select <b>Subscribe to new channels</b>.</li>
                                    <li>Click <b>Create new channel</b>. Enter the channel name and save.</li>
                                    <li>Navigate to <b>Settings</b> > <b>Organization/Personal settings</b> > <b>Channels</b>, and search the channel you just created.
                                        <ol>
                                            <li>Note down <b>Channel name</b></li>
                                        </ol>
                                    </li>
                                    <li>Navigate to the topic where you want bot to post the alert.
                                        <ol>
                                            <li>Note down <b>Topic name</b></li>
                                        </ol>
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                
                <b-row v-if="input.type == 11">
                    <b-col md="12">
                        <div class="note-section">
                            <div class="note-header-simple">
                                <div>
                                    <strong class="info-title">Info:</strong>
                                    <p class="info-text">
                                        <i class="fa fa-hand-o-right"></i>
                                        For more information 
                                        <a :href="$router.resolve({ path: '/app/teamsinstruction' }).href" target="_blank" class="info-link">
                                            Click here to see the instructions.
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>
            
            <!-- Submit Button -->
            <div class="form-actions">
                <button 
                    type="submit" 
                    class="create-alert-btn" 
                    :disabled="!canSubmit"
                    :class="{ 'disabled': !canSubmit }"
                >
                    {{ edit_alert ? 'Save Changes' : 'Create Now' }}
                </button>
            </div>
        </form>
    </div>
</template>


<script>
import AlertController from "../../services/alertController";
import { alertEvents } from '@/plugins/posthog-events';
export default{
    name : "AddAlerts",
    props:{add_alerts_modal:Boolean,edit_alert:Boolean,alert_data:Object},
    data(){
        return {
            disable_submit:false,
            input:{type:null,name:'',alertToUrl:''},
            expandedNotes: {
                email: false,
                skype: false,
                telegram: false,
                webpush: false,
                webhook: false,
                phone: false,
                slack: false,
                zapier: false,
                zulip: false,
                teams: false
            },
            alert_opt:[
              { value: null, text:'Please Select' },
              { value: 1, text:'Email', disabled:true },
              // { value: 2, text:'Skype', disabled:true  },
              { value: 3, text:'Telegram', disabled:true  },
              { value: 4, text:'Web push', disabled:true  },
              { value: 5, text:'Webhook', disabled:true  },
              { value: 6, text:'Message', disabled:true  },
              { value: 7, text:'Call', disabled:true  },
              { value: 8, text:'Slack', disabled:true  },
              { value: 9, text:'Zappier webhook', disabled:true  },
              { value: 10, text: 'Zulip', disabled: true },
              { value: 11, text: 'Teams', disabled: true }
            ],
            related_id_lable:[
              { value: null, text:'Please Select' },
              { value: '1', text:'Email Id ' },
              { value: '2', text:'Skype Id ' },
              { value: '3', text:'Telegram Id ' },
              { value: '4', text:'Web Push Url ' },
              { value: '5', text:'Webhook Url ' },
              { value: '6', text:'Contact Number ' },
              { value: '7', text:'Contact Number ' },
              { value: '8', text:'Slack User email ' },
              { value: '9', text:'Zappier Webhook Url' },
              { value: '10', text: 'Zulip bot email' },
              { value: '11', text: 'Teams Webhook Url' }
            ],
            plan_details:[],
            payload:{},
            invalid_fname_message: 'The name field is required',
            isNameEntered: false,
            isKeyEntered: false,
            invalid_key_message: 'The key field is required',
            isUrlEntered: false,
            invalid_url_message: 'The field is required',
            isZulipKeyEntered: false,
            invalid_zulip_key_message: 'The field is required',
            isSiteEntered: false,
            invalid_site_message: 'The site field is required',
            isChannelEntered: false,
            invalid_channel_message: 'The channel field is required',
            isTopicEntered: false,
            invalid_topic_message: 'The topic field is required',
        }
    },
    computed:{
        validate_type(){
            if(this.input.type == null){return false;}
            else{return true;}
        },
        inputPlaceholder(){
            if(this.input.type == 1){return 'Email Id'}
            else if(this.input.type == 2){return 'Skype Id'}
            else if(this.input.type == 3){return 'Telegram Id'}
            else if(this.input.type == 4){return 'Web Push Url'}
            else if(this.input.type == 5){return 'Webhook Url'}
            else if(this.input.type == 6 || this.input.type == 7){return 'Contact Number'}
            else if(this.input.type == 8){return 'Slack User email'}
            else if(this.input.type == 9){return 'Zappier Webhook Url'}
            else if(this.input.type == 10){return 'Zulip bot email'}
            else if(this.input.type == 11){return 'Teams Webhook Url'}
        },
        showAdditionalFields(){
            return this.input.type != null && this.input.name.trim() !== '';
        },
        canSubmit(){
            // Basic checks
            if (this.input.type == null || !this.input.name || this.input.name.trim() === '') {
                return false;
            }
            
            // Check name validation
            if (this.isNameEntered && this.invalid_fname_message) {
                return false;
            }
            
            // Check webhook secret key for type 5
            if (this.input.type == 5) {
                if (!this.input.sk_webhook || this.input.sk_webhook.trim() === '') {
                    return false;
                }
                if (this.isKeyEntered && this.invalid_key_message) {
                    return false;
                }
            }
            
            // Check Zulip fields for type 10
            if (this.input.type == 10) {
                if (!this.input.key || !this.input.site || !this.input.channel || !this.input.topic) {
                    return false;
                }
                if ((this.isZulipKeyEntered && this.invalid_zulip_key_message) ||
                    (this.isSiteEntered && this.invalid_site_message) ||
                    (this.isChannelEntered && this.invalid_channel_message) ||
                    (this.isTopicEntered && this.invalid_topic_message)) {
                    return false;
                }
            }
            
            // Check alertToUrl for all types except type 4 (Web push)
            if (this.input.type != 4) {
                if (!this.input.alertToUrl || this.input.alertToUrl.trim() === '') {
                    return false;
                }
                if (this.isUrlEntered && this.invalid_url_message) {
                    return false;
                }
            }
            
            return true;
        }
    },
    created(){
        this.plan_details = this.$root.$data.plan_details
        for(var i=0; i< this.alert_opt.length; i++ ){
            this.plan_details.alertsTypes.map((mt) => { 
                if(this.alert_opt[i].value == mt){
                    this.alert_opt[i].disabled = false;
                }
            })
        }
    },
    mounted(){
        this.$root.$data.alert_id = ''
        if(this.edit_alert){
            this.input = this.alert_data
            
            // Set validation flags for pre-filled fields to show validation icons
            if (this.input.name) {
                this.isNameEntered = true;
                this.invalid_fname_message = '';
            }
            
            if (this.input.sk_webhook && this.input.type == 5) {
                this.isKeyEntered = true;
                this.invalid_key_message = '';
            }
            
            if (this.input.alertToUrl && this.input.type != 4) {
                this.isUrlEntered = true;
                this.invalid_url_message = '';
            }
            
            // Zulip fields validation
            if (this.input.type == 10) {
                if (this.input.key) {
                    this.isZulipKeyEntered = true;
                    this.invalid_zulip_key_message = '';
                }
                if (this.input.site) {
                    this.isSiteEntered = true;
                    this.invalid_site_message = '';
                }
                if (this.input.channel) {
                    this.isChannelEntered = true;
                    this.invalid_channel_message = '';
                }
                if (this.input.topic) {
                    this.isTopicEntered = true;
                    this.invalid_topic_message = '';
                }
            }
        }
    },
    methods:{
        toggleNote(type) {
            this.expandedNotes[type] = !this.expandedNotes[type];
        },
        OpenTeamInstructions(){
            this.$root.$data.Teams_instructions = true
        },
        async onSubmit(){
            this.disable_submit = true
            this.payload = {"name": this.input.name,"type": this.input.type,"alertToUrl": this.input.alertToUrl}
            
            switch(this.input.type){
                case 1: 
                    if(this.validateEmail(this.input.alertToUrl)){
                        this.payload = {"name": this.input.name,"type": this.input.type,"alertToUrl": this.input.alertToUrl}
                    }
                    else{
                        this.payload = {}
                        this.$toasted.show("Invalid Email", {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                    }
                    break;
                case 3: 
                    let validateTelegram = this.isValidTelegramId(this.input.alertToUrl)
                    if(!validateTelegram.valid){
                        this.$toasted.show(validateTelegram.message, {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                        this.disable_submit = false
                        return;
                    }
                    break;
                case 4: 
                    this.payload = {"name": this.input.name,"type": this.input.type}
                    break;
                case 5: 
                    this.payload.sk_webhook = this.input.sk_webhook
                    break;
                case 8: 
                    if(!this.validateEmail(this.input.alertToUrl)){
                        this.$toasted.show("Invalid Email", {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                        this.disable_submit = false
                        return;
                    }
                    break;
                case 10:
                    if(this.validateEmail(this.input.alertToUrl)){
                        this.payload = {"name": this.input.name,"type": this.input.type,"alertToUrl": this.input.alertToUrl, "key": this.input.key, "site": this.input.site, "channel": this.input.channel, "topic": this.input.topic }
                    } else {
                        this.$toasted.show("Invalid Email", {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                        this.disable_submit = false
                        return;
                    }
                    break;
            }
            
            if(Object.keys(this.payload).length !== 0){
                var response
                if(this.edit_alert){
                    var p = { "acid": this.input._id, "changePayload":this.payload }
                    response = await AlertController.editAlert(p)
                }
                else{
                    response = await AlertController.createAlert(this.payload)
                    if(response.data && response.data.aid){
                        this.$root.$data.alert_id = response.data.aid
                    }
                }
                if(response.result){
                    // Track alert channel events in PostHog
                    const alertType = this.getAlertTypeName(this.input.type);
                    if(this.edit_alert){
                        alertEvents.ruleUpdated(alertType);
                    } else {
                        alertEvents.channelAdded(alertType);
                    }
                    this.$toasted.show(response.message, {theme: 'outline',position: "bottom-center", type: 'success', duration: 2000});
                    this.$emit('alertmodal')
                }
                else{
                    this.$toasted.show(response.message, {theme: 'outline',position: "bottom-center", type: 'error', duration: 2000});
                }
            }
            this.disable_submit = false
        },
        validateName(name) {
            this.isNameEntered = true;
            name = name ? name : this.input.name;
            
            let regexp = /^(?!\s*$)[a-zA-Z0-9 ._-]+$/;
            if (regexp.test(name)) {
                this.invalid_fname_message = '';
                return true;
            } else {
                this.invalid_fname_message = 'Please enter valid friendly name';
                return false;
            }
        },
        validateKey() {
            this.isKeyEntered = true;
            let key = this.input.sk_webhook;
            if (key && !key.includes(' ')) {
                this.invalid_key_message = '';
                return true;
            } else {
                this.invalid_key_message = 'Please enter a valid secret key without spaces';
                return false;
            }
        },
        validateZulipKey() {
            this.isZulipKeyEntered = true;
            if (this.input.key) {
                this.invalid_zulip_key_message = '';
                return true;
            } else {
                this.invalid_zulip_key_message = 'Please enter valid key';
                return false;
            }
        },
        validateChannel() {
            this.isChannelEntered = true;
            if (this.input.channel) {
                this.invalid_channel_message = '';
                return true;
            } else {
                this.invalid_channel_message = 'Please enter valid channel';
                return false;
            }
        },
        validateSite() {
            this.isSiteEntered = true;
            let siteRegex = /^(https?:\/\/)[^\s$.?#].[^\s]*$/;
            if (siteRegex.test(this.input.site)) {
                this.invalid_site_message = '';
                return true;
            } else {
                this.invalid_site_message = 'Please enter valid site url';
                return false;
            }
        },
        validateTopic() {
            this.isTopicEntered = true;
            if (this.input.topic) {
                this.invalid_topic_message = '';
                return true;
            } else {
                this.invalid_topic_message = 'Please enter valid topic';
                return false;
            }
        },
        validateAlertUrl() {
            this.isUrlEntered = true;
            const alertUrl = this.input.alertToUrl;
            if (!alertUrl || alertUrl.trim() === '') {
                this.invalid_url_message = 'The field is required';
                return false;
            }
            
            if(this.input.type == 1 || this.input.type == 8 || this.input.type == 10) {
                // Email validation for Email, Slack, and Zulip
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if(!emailRegex.test(alertUrl)) {
                    this.invalid_url_message = 'Please enter a valid email address';
                    return false;
                }
            } 
            else if (this.input.type == 3) {
                // Telegram validation
                const telegramRegex = /^[a-zA-Z0-9_]{5,32}$/;
                if(!telegramRegex.test(alertUrl)) {
                    this.invalid_url_message = 'Telegram username must be 5-32 characters long and contain only letters, numbers, and underscores';
                    return false;
                }
            } 
            else if (this.input.type == 5 || this.input.type == 9 || this.input.type == 11) {
                // URL validation
                const urlRegex = /^https?:\/\/.+/;
                if(!urlRegex.test(alertUrl)) {
                    this.invalid_url_message = 'Please enter a valid URL starting with http:// or https://';
                    return false;
                }
            } 
            else if (this.input.type == 6 || this.input.type == 7) {
                // Phone validation
                let newRegex =  /^\+?\d{10,13}$/;
                if(!newRegex.test(alertUrl)) {
                    this.invalid_url_message = 'Please enter a valid phone number';
                    return false;
                }
            } 
            // else if (this.input.type == 2) {
            //     // Skype validation
            //     const skypeRegex = /^(live:)?([a-zA-Z][a-zA-Z0-9._-]{5,31}|\.cid\.[a-f0-9]{16})$/;
            //     if(!skypeRegex.test(alertUrl)) {
            //         this.invalid_url_message = 'Please enter a valid Skype ID';
            //         return false;
            //     }
            // }
            else if (this.input.type == 4) {
                // Web push doesn't need URL validation
                return true;
            }
            else {
                this.invalid_url_message = 'Invalid alert type';
                return false;
            }

            this.invalid_url_message = '';
            return true;
        },
        validateEmail(email) {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return regex.test(email);
        },
        isValidTelegramId(input) {
            const regexPattern = /^[a-zA-Z0-9_]{5,32}$/;
            if (!input) {
                return { valid: false, message: "Telegram ID is required." };
            }
            if (input.length < 5 || input.length > 32) {
                return { valid: false, message: "Telegram ID must be between 5 and 32 characters." };
            }
            if (!regexPattern.test(input)) {
                return { valid: false, message: "Invalid Telegram ID. It can only contain letters, digits, and underscores." };
            }
            return { valid: true, message: "Valid Telegram ID." };
        },
        getAlertTypeName(type) {
            const types = {
                1: 'email',
                3: 'telegram',
                4: 'webpush',
                5: 'webhook',
                6: 'sms',
                7: 'call',
                8: 'slack',
                9: 'zapier',
                10: 'zulip',
                11: 'teams'
            };
            return types[type] || 'unknown';
        },
    }
}
</script>

<style scoped>
.add-alert-container {
  width: 100%;
}

/* Form Layout */
.form-step-one,
.form-step-two {
  width: 100%;
}

/* Form Group */
.form-group {
  margin-bottom: 26px !important;
}

.form-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 0.915em;
  color: #3A3A3A;
  margin-bottom: 10px;
  display: block;
}

.required {
  color: #E70000;
}

/* Custom Select Wrapper */
.custom-select-wrapper {
  position: relative;
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Disabled select wrapper */
.custom-select-wrapper:has(.custom-form-select:disabled) {
  background: #F3F4F6;
}

.select-icon {
  width: 24px;
  height: 24px;
}

.custom-form-select {
  border: none !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 0.915em;
  color: #3A3A3A;
  padding: 0 0 0 10px !important;
  height: auto;
  flex: 1;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.custom-form-select:focus {
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border: none !important;
}

.custom-form-select option {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
}

/* Remove autofill background color for select */
.custom-form-select:-webkit-autofill,
.custom-form-select:-webkit-autofill:hover,
.custom-form-select:-webkit-autofill:focus,
.custom-form-select:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  -webkit-text-fill-color: #3A3A3A !important;
  background-color: transparent !important;
}

.dropdown-arrow {
  position: absolute;
  right: 20px;
  pointer-events: none;
}

/* Custom Input Wrapper */
.custom-input-wrapper {
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  padding: 0 12px;
  height: 48px;
  display: flex;
  align-items: center;
  position: relative;
}

.custom-input-wrapper.has-validation {
  padding-right: 40px;
}

/* Validation Icon */
.validation-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.validation-icon svg {
  width: 20px;
  height: 20px;
}

.custom-form-input {
  border: none !important;
  background: transparent !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 0.915em;
  color: #3A3A3A;
  padding: 0 !important;
  height: auto;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.custom-form-input:focus {
  box-shadow: none !important;
  outline: none !important;
  background: transparent !important;
  border: none !important;
}

.custom-form-input::placeholder {
  color: #3A3A3A;
  opacity: 0.5;
}

/* Remove autofill background color */
.custom-form-input:-webkit-autofill,
.custom-form-input:-webkit-autofill:hover,
.custom-form-input:-webkit-autofill:focus,
.custom-form-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  -webkit-text-fill-color: #3A3A3A !important;
  transition: background-color 5000s ease-in-out 0s;
  background-color: transparent !important;
}

/* For other browsers */
.custom-form-input:autofill {
  background-color: transparent !important;
  color: #3A3A3A !important;
}


/* Notes Divider */
.notes-divider {
  border: none;
  border-top: 1px solid #E5E7EB;
  margin: 30px 0 20px 0;
  opacity: 1;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: center;
  padding-top: 0;
  margin-top: 30px;
}

/* Create Alert Button */
.create-alert-btn {
  background: #2FB234;
  border: none;
  border-radius: 12px;
  padding: 12px 40px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5em;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 48px;
}

.create-alert-btn:hover:not(:disabled) {
  background: #28A02D;
  transform: translateY(-1px);
}

.create-alert-btn.disabled,
.create-alert-btn:disabled {
  background: #7D8699;
  cursor: not-allowed;
  opacity: 1;
  transform: none;
}

/* Two Column Layout for Additional Fields */
.form-step-one .row,
.form-step-two .row {
  margin-bottom: 0;
}

.form-step-two .col-md-6 {
  padding-right: 12.5px;
  padding-left: 12.5px;
}

/* Invalid Feedback - Hidden since we show icons inside fields */
.invalid-feedback {
  display: none;
}

/* Note Section */
.note-section {
  background: #FFFFFF;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  margin-bottom: 26px;
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 10px 20px;
  cursor: pointer;
  user-select: none;
}

.note-header-simple {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  user-select: none;
}

.note-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 0.915em;
  color: #3A3A3A;
  margin: 0;
}

.note-title span:first-child {
  color: #F20707;
}

.note-arrow {
  transition: transform 0.3s ease;
}

.note-arrow.rotated {
  transform: rotate(180deg);
}

.note-content {
  padding: 0 20px 20px 20px;
  margin-top: 0;
}

.note-list {
  margin: 15px 0 0 0;
  padding-left: 20px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #3A3A3A;
  line-height: 1.6em;
  list-style-position: outside;
}

.note-list li {
  margin-bottom: 8px;
}

.note-list ol {
  margin-top: 5px;
  margin-bottom: 5px;
}

.note-list .text-info {
  color: #0062CC;
  font-weight: 500;
}

.note-list .text-danger {
  color: #F20707;
  font-weight: 500;
}

/* Info Message */
.info-message {
  border: 1px solid #B3D9FF;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 0;
}

.info-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #E70000;
  display: inline-block;
  margin-right: 8px;
}

.info-text {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #393939;
  margin: 0;
}

.info-link {
  color: #0066CC;
  text-decoration: none;
  font-weight: 500;
}

/* Responsive */
@media screen and (max-width: 768px) {
  .form-step-two .col-md-6 {
    padding-right: 15px;
    padding-left: 15px;
    margin-bottom: 20px;
  }
  
  .create-alert-btn {
    width: 100%;
  }
}
</style>